<template lang="pug">
  .modal.active(:class='{"modal-lg": ignoredData.extraFields}')
    .modal-overlay(@click='closeModal')
    .modal-container
      .modal-header
        button.btn.btn-clear.float-right(@click='closeModal')
        .modal-title.h5 {{modalTitle}} Resource
      .modal-body
        form.content.form-horizontal
          .form-group
            .col-3
              label.form-label(for='modal/Resource:title') Title
            .col-9
              input.form-input(
                ref='autofocus'
                id='modal/Resource:title'
                type='text'
                placeholder='Resource Title'
                v-model='title'
                v-validate='"required"')
              .form-input-error-message A title is required
          .form-group
            .col-3
              label.form-label(for='modal/Resource:categories') Categories
            .col-9
              multiselect(
                :options='ignoredData.categories'
                label='label'
                track-by='value'
                :multiple='true'
                :close-on-select='false'
                placeholder='Select Categories'
                v-model='categories'
                v-validate='"required"')
              .form-input-error-message A category is required
          .form-group
            .col-3
              label.form-label(for='modal/Resource:place') Address
            .col-9
              gmap-autocomplete.form-input(
                ref='address'
                id='modal/Resource:place'
                placeholder='Resource Address'
                @place_changed="setPlace")
          .form-group(:class='{hidden: !ignoredData.extraFields || !placeID}')
            .col-3
              label Set Street View
            .col-9
              gmap-street-view-panorama(
                ref='map'
                :position='{lat, lng}'
                :pov='pov'
                :pano='pano'
                @pov_changed='setPOV'
                @pano_changed='setPano'
                style='height: 300px')
          .form-group(v-if='ignoredData.extraFields')
            .col-3
              label.form-label(for='modal/Resource:website') Website
            .col-9
              input.form-input(
                id='modal/Resource:website'
                type='text'
                placeholder='Website'
                v-model='website')
          .form-group(v-if='ignoredData.extraFields')
            .col-3
              label.form-label(for='modal/Resource:phone') Phone
            .col-9
              input.form-input(
                id='modal/Resource:phone'
                type='text'
                placeholder='Phone Number'
                v-model='phone')
          .form-group
            .col-12
              label.form-label(for='modal/Resource:notes') Notes
              textarea.form-input(
                id='modal/Resource:notes'
                placeholder='Resource Notes (location details, hours, restrictions, etc)'
                rows=5
                v-model='notes')
      .modal-footer
        button.btn.float-left(@click='openIntakeConfig') Intake Config
        span.form-group
          label.form-switch
            input(type='checkbox' v-model='ignoredData.extraFields')
            i.form-icon
            | Show Extra Fields
        button.btn.btn-primary(:class='{loading: ignoredData.isLoading}' @click='submit') {{saveLabel}}
</template>





<script>
  import Vue from 'vue'
  import {mapState} from 'vuex'
  import {omit} from 'lodash'
  import util from '@/util'
  import * as VueGoogleMaps from 'vue2-google-maps'
  import Multiselect from 'vue-multiselect'
  import firebase from 'firebase'
  import lockr from 'lockr'
  import categories from '@/mixins/categories'
  require('firebase/firestore')

  let panoService

  Vue.use(VueGoogleMaps, {
    load: {
      key: 'AIzaSyD3BUYczjmcnPnJlFCtOKkvH4Y7f_9168Q',
      libraries: 'places'
    }
  })

  export default {
    name: 'modal-resource',

    components: {
      Multiselect
    },

    // We compute the following because this modal is used for both adding and editing
    computed: mapState({
      editingResource: 'editingResource',
      intakeConfig: 'intakeConfig',

      modalTitle () {
        return this.editingResource ? 'Edit' : 'Add'
      },

      saveLabel () {
        return this.editingResource ? 'Save Changes' : 'Add Resource'
      }
    }),

    created () {
      panoService = new window.google.maps.StreetViewService()
    },

    mounted () {
      Vue.nextTick(() => {
        let categories = []

        // Transforms data for use with special input fields
        if (this.editingResource) {
          // Set the intake form data
          this.$store.commit('setIntakeConfig', this.editingResource.intakeConfig)

          // Transform Google Place Search
          this.$el.querySelector('#modal\\/Resource\\:place').value = this.editingResource.formattedAddress
          Object.assign(this.$data, this.editingResource)

          // Transform vue-multiselect requires the entire {label,value} to compare, which we do here
          this.editingResource.categories.forEach((category) => {
            this.ignoredData.categories.some((compare) => {
              let found = false
              if (compare.value === category) {
                categories.push(compare)
                found = true
              }
              return found
            })
          })

          this.categories = categories
        } else {
          this.$store.commit('clearIntakeConfig')
        }

        // Select the first field and clear out validator (since the focus action may trigger a validation)
        this.$refs.autofocus.focus()
        window.google.maps.event.trigger(this.$refs.map.$panoObject, 'resize')
        Vue.nextTick(() => (this.$validator.reset()))
      })
    },

    watch: {
      ignoredData: {
        handler: (data) => lockr.set('resource-showExtraFields', data.extraFields),
        deep: true
      },

      // The pano is "rendered" but css-hidden. This causes render issues when it's finally shown as there were no dimensions.
      // This triggers a refresh
      placeID () {
        Vue.nextTick(() => window.google.maps.event.trigger(this.$refs.map.$panoObject, 'resize'))
      },

      // Remember the first of the last categories for faster data entry
      categories (data) {
        lockr.set('resource-lastCategory', [data[0]])
      }
    },

    data () {
      let lastCategory = lockr.get('resource-lastCategory')

      return {
        title: '',
        notes: '',
        categories: lastCategory || [],
        lat: 0,
        lng: 0,

        // A direct link to addresses Google Maps page, for direction purposes
        mapURL: '',
        placeID: '',
        website: '',
        phone: '',
        phoneLink: '',

        // The address as shown in the input field
        formattedAddress: '',
        pov: null,
        pano: null,

        ignoredData: {
          isLoading: false,
          extraFields: lockr.get('resource-showExtraFields') || false,

          /**
           * To add new categories, simply add a new {key, tooltip}.
           * - Add a corresponding `/static/img/map/${key}.png` marker image.
           * - For markers, @see https://mapicons.mapsmarker.com/
           * @type {Array}
           */
          categories
        }
      }
    },

    methods: {
      closeModal () {
        this.$store.commit('toggleResourceModal')
      },

      // Break up the Google Place Search data into its components
      setPlace (place) {
        this.pano = null
        this.lat = place.geometry.location.lat()
        this.lng = place.geometry.location.lng()
        this.formattedAddress = place.formatted_address
        this.mapURL = place.url
        this.placeID = place.place_id

        Vue.nextTick(() => this.setPano())
      },

      setPOV (pov) {
        this.pov = pov
      },

      setPano (pano) {
        if (pano) {
          this.pano = pano
        } else {
          // This service is required to get only outdoor street views
          // @see https://stackoverflow.com/questions/32256936/disable-indoor-view-of-buildings-in-google-maps-street-view
          panoService.getPanorama({
            location: {lat: this.lat, lng: this.lng},
            radius: 1000,
            source: window.google.maps.StreetViewSource.OUTDOOR
          }, (data, status) => {
            if (status === window.google.maps.StreetViewStatus.OK) {
              this.$refs.map.$panoObject.setPano(data.location.pano)
            }
          })
        }
      },

      // @TODO We should (dis|en)able the submit button live
      submit () {
        this.$validator.validateAll().then(result => {
          if (result) {
            const db = firebase.firestore()
            let ref
            let data = Object.assign({},
              omit(this.$data, ['ignoredData']),
              {
                savedBy: this.$store.state.user.uid,
                savedOn: firebase.database.ServerValue.TIMESTAMP
              }
            )

            let categories = []
            data.categories.forEach((item) => categories.push(item.value))
            data.categories = categories
            data.phoneLink = util.phoneLinkify(data.phone)
            data.key = data.key || db.collection('resource').doc()
            data.intakeConfig = this.intakeConfig

            // If we're editing then the key is known (a string), if not, then the key IS the ref
            if (typeof data.key === 'string') {
              ref = db.collection('resource').doc(data.key)
            } else {
              ref = data.key
              data.key = ref.id
            }
            ref.set(data, {merge: true})
            this.$store.commit('toggleResourceModal')
          }
        })
      },

      openIntakeConfig () {
        this.$store.commit('toggleIntakeConfigModal')
      }
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/modal/Resource.vue